import { config } from 'aws-sdk'
import { PatientSidebarItemKeys } from 'components/Patients/PatientSwitchableView/PatientSidebar/patientSidebarConsts'
import useSelectPatientView from 'components/Patients/PatientSwitchableView/useSelectPatientView'
import { useCallback } from 'react'
import { useCreateNoteMutation } from 'store/modules/PatientNotes'
import { trackEvent } from 'utils/analyticsUtils'
import { logError } from 'utils/logUtils'
import { mapToCreateNoteInput } from 'utils/mappers/noteMappers'
import { generateMediaAndUpload, getNewMediaBucket } from 'utils/mediaUtils'
import { v4 } from 'uuid'

export default () => {
  const { selectPatientView } = useSelectPatientView()
  const [createNote] = useCreateNoteMutation()

  /**
   * Creates a new patient note with the given media attachments.
   * Used internally after uploading files to storage.
   *
   * @param {Object} params - Parameters for the note
   * @param {string[]} params.attachments - Array of serialized attachment objects (JSON strings)
   *
   * @returns {Promise<void>} A promise that resolves once the note is created
   */
  const handleAddNote = useCallback(
    async ({ noteId, attachments }) => {
      const payload = mapToCreateNoteInput({
        id: noteId,
        noteContent: '',
        attachments
      })

      await createNote({ variables: payload })
      trackEvent('Patient notes - Note added with attachments', payload)
    },
    [createNote]
  )

  /**
   * Handles attaching media to a note.
   *
   * @param {Object} params - Parameters for the upload and note creation
   * @param {Array<Object>} params.attachments - Array of media attachment objects
   * @param {string} params.attachments[].contentType - MIME type of the file
   * @param {string} params.attachments[].data - Base64 or binary file data
   * @param {string} params.attachments[].key - The S3 key or media identifier
   * @param {string} params.attachments[].type - Logical type of the attachment (e.g., 'image', 'video')
   * @param {string} params.scanId - The scanId the scan summary associated with, used for private media enforcement
   *
   * @returns {Promise<void>} Resolves after upload and note creation
   */
  const handleAddAttachmentsToNote = useCallback(
    async ({ attachments }) => {
      try {
        const newNoteId = v4()
        trackEvent('Scan - Attach images to note clicked', {
          numOfAttachments: attachments.length
        })

        await Promise.all(
          attachments.map(attachment =>
            generateMediaAndUpload({
              contentType: attachment.contentType,
              data: attachment.data,
              keySuffix: attachment.key,
              objectIdToAuthorize: newNoteId,
              type: 'noteAttachments'
            })
          )
        )

        await handleAddNote({
          noteId: newNoteId,
          attachments: JSON.stringify(
            attachments.map(attachment => ({
              type: attachment.type,
              s3Object: {
                key: `private/noteAttachments/${newNoteId}/${attachment.key}`,
                bucket: getNewMediaBucket(),
                region: config.aws_user_files_s3_bucket_region
              }
            }))
          )
        })

        selectPatientView(PatientSidebarItemKeys.Notes)
      } catch (error) {
        logError('An error occurred while trying to add attachments to a note', { error })
      }
    },
    [handleAddNote, selectPatientView]
  )

  return {
    handleAddAttachmentsToNote
  }
}
